import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Bonus, BonusConfig, BonusInstance, BonusInstanceWithDetails, ApiResponse, BonusDispatchLog, BonusAuditEvent } from '@/types'

export interface BonusWithDetails extends Bonus {
  goal_metric_name?: string
  goal_timeframe?: string
  goal_period_start?: number
  goal_period_end?: number
  user_name?: string
}

// Fetch bonuses (admin sees all, users see own)
export function useBonuses(filters?: { user_id?: string; goal_id?: string }) {
  const params = new URLSearchParams()
  if (filters?.user_id) params.set('user_id', filters.user_id)
  if (filters?.goal_id) params.set('goal_id', filters.goal_id)

  return useQuery({
    queryKey: ['bonuses', filters],
    queryFn: async (): Promise<BonusWithDetails[]> => {
      const url = params.toString() ? `/api/bonuses?${params}` : '/api/bonuses'
      const res = await fetch(url)
      const data: ApiResponse<BonusWithDetails[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch bonuses')
      return data.data!
    },
  })
}

// Create bonus (admin only)
export function useCreateBonus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (bonus: { goal_id: string; user_id: string; amount: number; currency?: string }) => {
      const res = await fetch('/api/bonuses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bonus),
      })
      const data: ApiResponse<Bonus> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to create bonus')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonuses'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Update bonus (admin only)
export function useUpdateBonus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...bonus }: Partial<Bonus> & { id: string }) => {
      const res = await fetch(`/api/bonuses/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bonus),
      })
      const data: ApiResponse<Bonus> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to update bonus')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonuses'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Delete bonus (admin only)
export function useDeleteBonus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/bonuses/${id}`, { method: 'DELETE' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to delete bonus')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonuses'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Response type for webhook trigger
export interface TriggerWebhookResponse {
  status: 'accepted' | 'rejected' | 'failed'
  trace_id: string
  dispatch_log_id: string
  error?: string
  idempotent?: boolean
  message?: string
}

// Trigger HR webhook for achieved bonuses (admin only)
export function useTriggerBonusWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (bonusId: string) => {
      const res = await fetch(`/api/bonuses/${bonusId}/trigger-webhook`, { method: 'POST' })
      const data: ApiResponse<TriggerWebhookResponse> = await res.json()
      if (!data.success && !data.data?.idempotent) throw new Error(data.error || 'Failed to trigger webhook')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonuses'] })
      queryClient.invalidateQueries({ queryKey: ['bonus-dispatch-history'] })
      queryClient.invalidateQueries({ queryKey: ['bonus-audit-events'] })
    },
  })
}

// Fetch dispatch history for a bonus (admin only)
export function useBonusDispatchHistory(bonusId: string, enabled = true) {
  return useQuery({
    queryKey: ['bonus-dispatch-history', bonusId],
    queryFn: async () => {
      const res = await fetch(`/api/bonuses/${bonusId}/dispatch-history`)
      const data: ApiResponse<{ logs: BonusDispatchLog[]; total: number }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch dispatch history')
      return data.data!
    },
    enabled: enabled && !!bonusId,
  })
}

// Fetch audit events for a bonus (admin only)
export function useBonusAuditEvents(bonusId: string, enabled = true) {
  return useQuery({
    queryKey: ['bonus-audit-events', bonusId],
    queryFn: async () => {
      const res = await fetch(`/api/bonuses/${bonusId}/audit-events`)
      const data: ApiResponse<{ events: BonusAuditEvent[]; total: number }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch audit events')
      return data.data!
    },
    enabled: enabled && !!bonusId,
  })
}

// ==================== BONUS CONFIGS (NEW) ====================

export interface BonusConfigWithDetails extends BonusConfig {
  goal_metric_name?: string
  goal_timeframe?: string
  user_name?: string
  instance_count?: number
  met_count?: number
}

// Fetch bonus configs (admin sees all, users see own)
// Use archived: true to fetch archived configs, false/undefined for active
export function useBonusConfigs(filters?: { user_id?: string; goal_id?: string; archived?: boolean }) {
  const params = new URLSearchParams()
  if (filters?.user_id) params.set('user_id', filters.user_id)
  if (filters?.goal_id) params.set('goal_id', filters.goal_id)
  if (filters?.archived) params.set('archived', 'true')

  return useQuery({
    queryKey: ['bonus-configs', filters],
    queryFn: async (): Promise<BonusConfigWithDetails[]> => {
      const url = params.toString() ? `/api/bonus-configs?${params}` : '/api/bonus-configs'
      const res = await fetch(url)
      const data: ApiResponse<BonusConfigWithDetails[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch bonus configs')
      return data.data!
    },
  })
}

// Create bonus config (admin only)
export function useCreateBonusConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (config: { goal_id: string; user_id: string; amount: number; currency?: string; is_recurring?: boolean }) => {
      const res = await fetch('/api/bonus-configs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      const data: ApiResponse<{ id: string }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to create bonus config')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus-configs'] })
      queryClient.invalidateQueries({ queryKey: ['bonuses'] })
    },
  })
}

// Update bonus config (admin only)
export function useUpdateBonusConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...config }: { id: string; amount?: number; currency?: string }) => {
      const res = await fetch(`/api/bonus-configs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      const data: ApiResponse<BonusConfig> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to update bonus config')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus-configs'] })
      queryClient.invalidateQueries({ queryKey: ['bonuses'] })
    },
  })
}

// Delete bonus config (admin only)
export function useDeleteBonusConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/bonus-configs/${id}`, { method: 'DELETE' })
      const data: ApiResponse<void> & { suggest_archive?: boolean } = await res.json()
      if (!data.success) {
        const error = new Error(data.error || 'Failed to delete bonus config') as Error & { suggest_archive?: boolean }
        error.suggest_archive = data.suggest_archive
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus-configs'] })
      queryClient.invalidateQueries({ queryKey: ['bonuses'] })
    },
  })
}

// Archive bonus config (admin only) - permanent, excludes from future evaluation
export function useArchiveBonusConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/bonus-configs/${id}/archive`, { method: 'POST' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to archive bonus config')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus-configs'] })
      queryClient.invalidateQueries({ queryKey: ['bonuses'] })
    },
  })
}

// Backfill historical periods for a bonus config (admin only)
export interface BackfillResponse {
  instances_created: number
  periods_skipped: number
  instance_ids: string[]
  errors: string[]
}

export function useBackfillBonusConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (configId: string) => {
      const res = await fetch(`/api/bonus-configs/${configId}/backfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true }),
      })
      const data: ApiResponse<BackfillResponse> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to backfill periods')
      return data.data!
    },
    onSuccess: (_, configId) => {
      queryClient.invalidateQueries({ queryKey: ['bonus-configs'] })
      queryClient.invalidateQueries({ queryKey: ['bonus-instances', configId] })
    },
  })
}

// ==================== BONUS INSTANCES (NEW) ====================

// Fetch instances for a config
export function useBonusInstances(configId: string, enabled = true) {
  return useQuery({
    queryKey: ['bonus-instances', configId],
    queryFn: async () => {
      const res = await fetch(`/api/bonus-configs/${configId}/instances`)
      const data: ApiResponse<{ instances: BonusInstance[]; total: number; limit: number; offset: number }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch instances')
      return data.data!
    },
    enabled: enabled && !!configId,
  })
}

// Fetch single instance with details
export function useBonusInstance(instanceId: string, enabled = true) {
  return useQuery({
    queryKey: ['bonus-instance', instanceId],
    queryFn: async () => {
      const res = await fetch(`/api/bonus-instances/${instanceId}`)
      const data: ApiResponse<BonusInstanceWithDetails> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch instance')
      return data.data!
    },
    enabled: enabled && !!instanceId,
  })
}

// Dispatch webhook for instance
export function useTriggerInstanceWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (instanceId: string) => {
      const res = await fetch(`/api/bonus-instances/${instanceId}/dispatch`, { method: 'POST' })
      const data: ApiResponse<TriggerWebhookResponse> = await res.json()
      if (!data.success && !data.data?.idempotent) throw new Error(data.error || 'Failed to dispatch')
      return data.data!
    },
    onSuccess: (_, instanceId) => {
      queryClient.invalidateQueries({ queryKey: ['bonus-instance', instanceId] })
      queryClient.invalidateQueries({ queryKey: ['bonus-instances'] })
      queryClient.invalidateQueries({ queryKey: ['instance-dispatch-history', instanceId] })
      queryClient.invalidateQueries({ queryKey: ['instance-audit-events', instanceId] })
    },
  })
}

// Instance dispatch history
export function useInstanceDispatchHistory(instanceId: string, enabled = true) {
  return useQuery({
    queryKey: ['instance-dispatch-history', instanceId],
    queryFn: async () => {
      const res = await fetch(`/api/bonus-instances/${instanceId}/dispatch-history`)
      const data: ApiResponse<{ logs: BonusDispatchLog[]; total: number }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch history')
      return data.data!
    },
    enabled: enabled && !!instanceId,
  })
}

// Instance audit events
export function useInstanceAuditEvents(instanceId: string, enabled = true) {
  return useQuery({
    queryKey: ['instance-audit-events', instanceId],
    queryFn: async () => {
      const res = await fetch(`/api/bonus-instances/${instanceId}/audit-events`)
      const data: ApiResponse<{ events: BonusAuditEvent[]; total: number }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch events')
      return data.data!
    },
    enabled: enabled && !!instanceId,
  })
}
